import React from 'react';
import { Box, Typography } from '@mui/material';

// ── רכיב תוכן אחרי הספירה (הרחמן / תהלים / אנא בכח) ─────────────────────────
const AfterCountSection = ({ section }) => (
  <Box sx={{ mt: 1.5, borderTop: '1px solid rgba(230,196,94,0.3)', pt: 1.5 }}>
    <Typography sx={{
      fontFamily: 'Frank Ruhl Libre, serif',
      fontSize: '0.6rem',
      color: '#e6c45e',
      letterSpacing: '0.1em',
      mb: 0.5,
      textAlign: 'center',
      opacity: 0.75,
    }}>
      — {section.title} —
    </Typography>
    {section.lines.map((line) => (
      <Typography key={line.id} sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: '0.65rem',
        color: 'rgba(255,248,220,0.85)',
        lineHeight: 1.8,
        textAlign: 'center',
        direction: 'rtl',
      }}>
        {line.text}
      </Typography>
    ))}
  </Box>
);

// ── תבנית אלגנטית — נייבי עמוק + זהב ────────────────────────────────────────
const Elegant = ({
  omerDay, isOmerSeason,
  isPreview, previewCountdown,
  mainCountText, subCountText, omerDayHebrew, sefirahName,
  showBracha, brachaText,
  showLeshemYichud, leshemYichudLines,
  showPasuk, pasukText,
  showAfterCount, afterCountSections,
  offSeasonMessage,
  title, showTitle,
  bgColor,
}) => {
  const bg = bgColor || '#0f1e35';

  if (!isOmerSeason) {
    return (
      <Box sx={{
        background: bg,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 1,
        padding: 2,
        direction: 'rtl',
      }}>
        <Typography sx={{ color: '#e6c45e', opacity: 0.6, fontSize: '1.1rem', fontFamily: 'Frank Ruhl Libre, serif' }}>
          🌾
        </Typography>
        <Typography sx={{
          color: 'rgba(255,248,220,0.5)',
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: '0.85rem',
          textAlign: 'center',
        }}>
          {offSeasonMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      background: bg,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: '12px 16px',
      direction: 'rtl',
      overflowY: 'auto',
      '&::-webkit-scrollbar': { width: 3 },
      '&::-webkit-scrollbar-thumb': { background: 'rgba(230,196,94,0.3)', borderRadius: 2 },
    }}>
      {/* קו עליון */}
      <Box sx={{ width: '80%', height: 2, background: 'linear-gradient(90deg, transparent, #e6c45e, transparent)', mb: 1.5 }} />

      {/* באנר ספירה לאחור */}
      {isPreview && previewCountdown && (
        <Box sx={{
          mb: 1.5, px: 2, py: 0.75,
          background: 'rgba(230,196,94,0.1)',
          border: '1px solid rgba(230,196,94,0.25)',
          borderRadius: 1,
        }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.72rem',
            color: '#e6c45e',
            textAlign: 'center',
            direction: 'rtl',
          }}>
            {previewCountdown}
          </Typography>
        </Box>
      )}

      {/* כותרת */}
      {showTitle && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: '0.7rem',
          color: 'rgba(230,196,94,0.7)',
          letterSpacing: '0.15em',
          mb: 1,
        }}>
          {title}
        </Typography>
      )}

      {/* לשם ייחוד */}
      {showLeshemYichud && (
        <Box sx={{
          mb: 1.5,
          px: 2,
          py: 1,
          border: '1px solid rgba(230,196,94,0.2)',
          borderRadius: 1,
          width: '100%',
          maxWidth: 420,
        }}>
          {leshemYichudLines.map((line, i) => (
            <Typography key={i} sx={{
              fontFamily: 'Frank Ruhl Libre, serif',
              fontSize: '0.58rem',
              color: 'rgba(255,248,220,0.65)',
              lineHeight: 1.9,
              textAlign: 'center',
              direction: 'rtl',
            }}>
              {line}
            </Typography>
          ))}
        </Box>
      )}

      {/* פסוק */}
      {showPasuk !== 'none' && pasukText && (
        <Box sx={{ mb: 1.5, px: 1 }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.62rem',
            color: 'rgba(230,196,94,0.75)',
            lineHeight: 2,
            textAlign: 'center',
            fontStyle: 'italic',
            direction: 'rtl',
          }}>
            {pasukText}
          </Typography>
        </Box>
      )}

      {/* ברכה */}
      {showBracha && (
        <Box sx={{
          mb: 1.5,
          px: 2,
          py: 1,
          background: 'rgba(230,196,94,0.07)',
          borderRadius: 1,
          width: '100%',
          maxWidth: 420,
        }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.72rem',
            color: 'rgba(255,248,220,0.9)',
            lineHeight: 2,
            textAlign: 'center',
            direction: 'rtl',
          }}>
            {brachaText}
          </Typography>
        </Box>
      )}

      {/* מספר בולט */}
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: 'clamp(3.5rem, 12vw, 6rem)',
        fontWeight: 700,
        color: '#e6c45e',
        lineHeight: 1,
        textShadow: '0 0 30px rgba(230,196,94,0.4)',
        letterSpacing: '-0.02em',
      }}>
        {omerDayHebrew}
      </Typography>

      {/* שורת ספירה ראשית */}
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: 'clamp(1rem, 3.5vw, 1.5rem)',
        color: 'rgba(255,248,220,0.95)',
        mt: 0.5,
        textAlign: 'center',
        direction: 'rtl',
        letterSpacing: '0.03em',
      }}>
        {mainCountText}
      </Typography>

      {/* שבועות+ימים */}
      {subCountText && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: 'clamp(0.75rem, 2.5vw, 1rem)',
          color: '#e6c45e',
          mt: 0.5,
          textAlign: 'center',
          direction: 'rtl',
          opacity: 0.9,
        }}>
          {subCountText}
        </Typography>
      )}

      {/* שם הספירה */}
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: '0.7rem',
        color: 'rgba(230,196,94,0.6)',
        mt: 1,
        fontStyle: 'italic',
        letterSpacing: '0.05em',
      }}>
        {sefirahName}
      </Typography>

      {/* תפילות אחרי ספירה */}
      {showAfterCount && afterCountSections.length > 0 && (
        <Box sx={{ width: '100%', maxWidth: 420, mt: 0.5 }}>
          {afterCountSections.map((section) => (
            <AfterCountSection key={section.id} section={section} />
          ))}
        </Box>
      )}

      {/* קו תחתון */}
      <Box sx={{ width: '80%', height: 2, background: 'linear-gradient(90deg, transparent, #e6c45e, transparent)', mt: 'auto', pt: 1.5 }} />
    </Box>
  );
};

export default Elegant;
